import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { SwapButton } from '@root/entities/trade-type/ui'
import { useTranslation } from '@root/shared/localization'
import { Input } from '@root/shared/ui-kit'

import {
  instantSellQuickOptionsAtom,
  reatomInstantSell,
} from '../../atoms/reatom-instant-sell'

import { InstantTradeBottomDirectionLabel } from './instant-trade-bottom-direction-label'
import { InstantTradeBottomGas } from './instant-trade-bottom-gas'
import { InstantTradeBottomQuickOption } from './instant-trade-bottom-quick-option'

import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  pair: PairModel
} & ClassNameProps

export const InstantTradeBottomSell: React.FC<Props> = ({
  className,
  pair,
}) => {
  const { t } = useTranslation()

  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomInstantSell({ pair }),
    [pair],
  )
  const { inputModel } = model
  const [amountBalancePercentage] = useAtom(
    inputModel.amountBalancePercentageAtom,
  )
  const [amountRaw] = useAtom(inputModel.amountRawAtom)

  const [requestState] = useAtom(model.executeSwapModel)
  const [validationError] = useAtom(model.validationAtom)
  const initialize = useAction(model.initialize)

  const setAmountRaw = useAction(inputModel.setAmountRawAction)
  const setQuickAmount = useAction(inputModel.setQuickAmount)
  const executeSell = useAction(model.executeSwapModel.execute)
  const [instantSellQuickOptions] = useAtom(instantSellQuickOptionsAtom)

  useEffect(() => {
    const dispose = initialize()
    return () => {
      dispose()
    }
  }, [initialize, model])

  return (
    <Root className={className}>
      <InstantTradeBottomDirectionLabel direction="sell" pair={pair} />

      <InputContainer>
        <AmountInput
          numeric
          startContent={
            <InputLabel>
              <QuickOptionsContainer>
                {instantSellQuickOptions.map((it) => (
                  <InstantTradeBottomQuickOption
                    label={`${it.toNumber()}%`}
                    active={
                      amountBalancePercentage.toFixed(2) === it.toFixed(2)
                    }
                    key={it.toNumber()}
                    onClick={() => setQuickAmount(it.toNumber())}
                  />
                ))}
              </QuickOptionsContainer>
              <span
                css={css`
                  font-weight: 400;
                `}
              >
                {t('common.amount')}
              </span>
            </InputLabel>
          }
          decimalScale={pair.quote.decimals}
          onChange={(e) => setAmountRaw(e.target.value)}
          value={amountRaw}
          size="m"
          endContent={
            <CurrencyIcon
              currency={pair.quote}
              size={24}
              withGoogleLens={false}
              css={css`
                justify-self: end;
              `}
            />
          }
        />
      </InputContainer>
      <SellButton
        swapDirection="sell"
        onClick={() => executeSell()}
        disabled={!!validationError}
        busy={AsyncStateN.isLoading(requestState)}
      >
        {t('trading.sell')}
      </SellButton>
      <InstantTradeBottomGas swapDirection="sell" />
    </Root>
  )
}

const Root = styled.div`
  padding-inline: 12px 8px;
  align-content: center;

  display: grid;
  grid-auto-flow: column;
`

const SellButton = styled(SwapButton)`
  width: 62px;
  height: 36px;
`

const AmountInput = styled(Input)`
  width: 280px;
  --input-height: 36px;
  --input-padding-inline: 3px 6px;
`

const InputContainer = styled.div`
  padding-top: 1px;
  margin-inline-end: 6px;
`

const QuickOptionsContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 2px;
`

const InputLabel = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 10px;
  align-items: center;
`
